'use client';

// ============================================================================
// CrewAhead Maritime Platform - useCareerIntelligence Hook
// ============================================================================
// Production-ready hook for the career intelligence feature.
// Manages career transition profile, readiness scores, career paths,
// skill gaps, course recommendations, and shore job matches.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getCareerTransitionProfile as getCareerTransitionProfileApi,
  createCareerTransitionProfile as createCareerTransitionProfileApi,
  updateCareerTransitionProfile as updateCareerTransitionProfileApi,
  updateCareerTargetPositions as updateCareerTargetPositionsApi,
  activateTransitionProfile as activateTransitionProfileApi,
  deactivateTransitionProfile as deactivateTransitionProfileApi,
  getCareerSummary as getCareerSummaryApi,
  getReadinessScore as getReadinessScoreApi,
  getCareerPath as getCareerPathApi,
  getSkillGaps as getSkillGapsApi,
  getRecommendedCourses as getRecommendedCoursesApi,
  getShoreJobMatches as getShoreJobMatchesApi,
  getCareerBarriers as getCareerBarriersApi,
  recalculateRecommendations as recalculateRecommendationsApi,
} from '../lib/api';
import type { CrewAheadApiError } from '../lib/api/client';
import { QUERY_KEYS } from '../lib/types/api';
import { useAuthStore } from '../store/authStore';
import type {
  CareerTransitionProfile,
  ReadinessScore,
  CareerPath,
  SkillGapResult,
  CourseRecommendation,
  CareerBarrierResult,
  ShoreJobMatch,
  SeaToShoreSummary,
} from '../lib/types';

// ─── Retry Policy ───────────────────────────────────────────────────────────

const DEFAULT_RETRY_COUNT = 2;

function shouldRetry(failureCount: number, error: CrewAheadApiError): boolean {
  if (failureCount >= DEFAULT_RETRY_COUNT) return false;

  const status = error?.statusCode;

  // Do not retry client errors (4xx) except 408/429.
  if (status !== undefined && status >= 400 && status < 500) {
    return status === 408 || status === 429;
  }

  // Retry network errors and 5xx.
  return true;
}

// ─── Hook Return Type ────────────────────────────────────────────────────────

export interface UseCareerIntelligenceReturn {
  // ─── Profile ──────────────────────────────────────────────────────────────
  transitionProfile: CareerTransitionProfile | null;
  isProfileLoading: boolean;
  isProfileFetching: boolean;
  isTransitionActive: boolean;
  createProfile: (data: Partial<CareerTransitionProfile>) => Promise<CareerTransitionProfile>;
  updateProfile: (data: Partial<CareerTransitionProfile>) => Promise<CareerTransitionProfile>;
  updateTargetPositions: (targetPositionIds: number[]) => Promise<CareerTransitionProfile>;
  activateTransition: (targetPositionIds: number[]) => Promise<void>;
  deactivateTransition: () => Promise<void>;
  isProfileMutating: boolean;
  profileError: CrewAheadApiError | null;

  // ─── Summary (dashboard widget data) ──────────────────────────────────────
  summary: SeaToShoreSummary | null;
  isSummaryLoading: boolean;
  isSummaryFetching: boolean;
  summaryError: CrewAheadApiError | null;

  // ─── Readiness ────────────────────────────────────────────────────────────
  readinessScore: ReadinessScore | null;
  isReadinessLoading: boolean;
  isReadinessFetching: boolean;
  readinessError: CrewAheadApiError | null;

  // ─── Career Path ──────────────────────────────────────────────────────────
  careerPath: CareerPath | null;
  isCareerPathLoading: boolean;
  isCareerPathFetching: boolean;
  careerPathError: CrewAheadApiError | null;

  // ─── Skill Gaps ───────────────────────────────────────────────────────────
  skillGaps: SkillGapResult | null;
  isSkillGapsLoading: boolean;
  isSkillGapsFetching: boolean;
  skillGapsError: CrewAheadApiError | null;

  // ─── Course Recommendations ───────────────────────────────────────────────
  courses: CourseRecommendation[];
  isCoursesLoading: boolean;
  isCoursesFetching: boolean;
  coursesError: CrewAheadApiError | null;

  // ─── Barriers ─────────────────────────────────────────────────────────────
  barriers: CareerBarrierResult | null;
  isBarriersLoading: boolean;
  isBarriersFetching: boolean;
  barriersError: CrewAheadApiError | null;

  // ─── Shore Job Matches ────────────────────────────────────────────────────
  shoreJobs: ShoreJobMatch[];
  isShoreJobsLoading: boolean;
  isShoreJobsFetching: boolean;
  shoreJobsError: CrewAheadApiError | null;

  // ─── Active Target Position ───────────────────────────────────────────────
  targetPositionId: number | undefined;
  setTargetPositionId: (id: number | undefined) => void;

  // ─── Recalculate ──────────────────────────────────────────────────────────
  recalculate: () => Promise<void>;
  isRecalculating: boolean;
  recalculateError: CrewAheadApiError | null;

  // ─── Refetch Helpers ──────────────────────────────────────────────────────
  refetchAll: () => Promise<void>;
}

// ─── useCareerIntelligence Hook ──────────────────────────────────────────────

/**
 * Hook for managing career transition intelligence.
 *
 * Provides queries and mutations for the sea-to-shore career transition
 * feature. All intelligence queries (readiness, career path, skill gaps,
 * courses) are gated behind the transition profile being active. The
 * summary query always fetches regardless of activation state.
 *
 * @param options - Optional initial target position ID
 *
 * @example
 * ```tsx
 * const {
 *   transitionProfile,
 *   isTransitionActive,
 *   readinessScore,
 *   careerPath,
 *   skillGaps,
 *   courses,
 *   targetPositionId,
 *   setTargetPositionId,
 * } = useCareerIntelligence({ targetPositionId: 3 });
 *
 * return (
 *   <Dashboard>
 *     <CareerSummary summary={summary} />
 *     {isTransitionActive && (
 *       <CareerPathView path={careerPath} readiness={readinessScore} />
 *     )}
 *   </Dashboard>
 * );
 * ```
 */
export function useCareerIntelligence(options?: {
  targetPositionId?: number;
}): UseCareerIntelligenceReturn {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ─── Local State ───────────────────────────────────────────────────────────

  const [targetPositionId, setTargetPositionIdState] = useState<number | undefined>(
    options?.targetPositionId
  );

  /**
   * Update the active target position and immediately invalidate dependent
   * queries so stale career path and skill gap data is not displayed while
   * the new target is loading.
   */
  const setTargetPositionId = useCallback(
    (id: number | undefined) => {
      setTargetPositionIdState(id);
      if (mountedRef.current) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.CAREER.PATH(id),
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.CAREER.SKILL_GAPS(id),
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.CAREER.READINESS(id),
        });
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.CAREER.COURSES(id),
        });
      }
    },
    [queryClient]
  );

  // ─── Profile Query ─────────────────────────────────────────────────────────

  const {
    data: transitionProfileData,
    isLoading: isProfileLoading,
    isFetching: isProfileFetching,
    refetch: refetchProfileQuery,
    error: profileError,
  } = useQuery<CareerTransitionProfile | null, CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.PROFILE,
    queryFn: async () => {
      try {
        return await getCareerTransitionProfileApi();
      } catch (err: unknown) {
        // 404 = no profile yet; return null instead of throwing
        const apiErr = err as { statusCode?: number };
        if (apiErr?.statusCode === 404) return null;
        throw err;
      }
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: shouldRetry,
  });

  // Default to the first target position when the profile loads and no
  // explicit target has been chosen.
  useEffect(() => {
    if (
      mountedRef.current &&
      targetPositionId === undefined &&
      transitionProfileData?.targetShorePositionsDetail?.length
    ) {
      setTargetPositionIdState(transitionProfileData.targetShorePositionsDetail[0].id);
    }
  }, [transitionProfileData, targetPositionId]);

  // ─── Summary Query ─────────────────────────────────────────────────────────

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
    refetch: refetchSummaryQuery,
    error: summaryError,
  } = useQuery<SeaToShoreSummary, CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.SUMMARY,
    queryFn: getCareerSummaryApi,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 10,
    retry: shouldRetry,
  });

  // ─── Readiness Score Query ─────────────────────────────────────────────────

  const {
    data: readinessData,
    isLoading: isReadinessLoading,
    isFetching: isReadinessFetching,
    refetch: refetchReadinessQuery,
    error: readinessError,
  } = useQuery<ReadinessScore, CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.READINESS(targetPositionId),
    queryFn: () => getReadinessScoreApi(targetPositionId),
    enabled: isAuthenticated && transitionProfileData?.isActive === true,
    staleTime: 1000 * 60 * 10,
    retry: shouldRetry,
  });

  // ─── Career Path Query ─────────────────────────────────────────────────────

  const {
    data: careerPathData,
    isLoading: isCareerPathLoading,
    isFetching: isCareerPathFetching,
    refetch: refetchCareerPathQuery,
    error: careerPathError,
  } = useQuery<CareerPath, CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.PATH(targetPositionId),
    queryFn: () => getCareerPathApi(targetPositionId),
    enabled: isAuthenticated && transitionProfileData?.isActive === true,
    staleTime: 1000 * 60 * 10,
    retry: shouldRetry,
  });

  // ─── Skill Gaps Query ──────────────────────────────────────────────────────

  const {
    data: skillGapsData,
    isLoading: isSkillGapsLoading,
    isFetching: isSkillGapsFetching,
    refetch: refetchSkillGapsQuery,
    error: skillGapsError,
  } = useQuery<SkillGapResult, CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.SKILL_GAPS(targetPositionId),
    queryFn: () => getSkillGapsApi(targetPositionId),
    enabled: isAuthenticated && transitionProfileData?.isActive === true,
    staleTime: 1000 * 60 * 10,
    retry: shouldRetry,
  });

  // ─── Course Recommendations Query ──────────────────────────────────────────

  const {
    data: coursesData,
    isLoading: isCoursesLoading,
    isFetching: isCoursesFetching,
    refetch: refetchCoursesQuery,
    error: coursesError,
  } = useQuery<CourseRecommendation[], CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.COURSES(targetPositionId),
    queryFn: () => getRecommendedCoursesApi(targetPositionId),
    enabled: isAuthenticated && transitionProfileData?.isActive === true,
    staleTime: 1000 * 60 * 10,
    retry: shouldRetry,
  });

  // ─── Transition Barriers Query ─────────────────────────────────────────────

  const {
    data: barriersData,
    isLoading: isBarriersLoading,
    isFetching: isBarriersFetching,
    refetch: refetchBarriersQuery,
    error: barriersError,
  } = useQuery<CareerBarrierResult, CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.BARRIERS,
    queryFn: getCareerBarriersApi,
    enabled: isAuthenticated && transitionProfileData?.isActive === true,
    staleTime: 1000 * 60 * 10,
    retry: shouldRetry,
  });

  // ─── Shore Job Matches Query ───────────────────────────────────────────────

  const {
    data: shoreJobsData,
    isLoading: isShoreJobsLoading,
    isFetching: isShoreJobsFetching,
    refetch: refetchShoreJobsQuery,
    error: shoreJobsError,
  } = useQuery<ShoreJobMatch[], CrewAheadApiError>({
    queryKey: QUERY_KEYS.CAREER.SHORE_JOBS,
    queryFn: () => getShoreJobMatchesApi(),
    enabled: isAuthenticated && transitionProfileData?.isActive === true,
    staleTime: 1000 * 60 * 10,
    retry: shouldRetry,
  });

  // ─── Profile Mutations ─────────────────────────────────────────────────────

  const createProfileMutation = useMutation<
    CareerTransitionProfile,
    CrewAheadApiError,
    Partial<CareerTransitionProfile>
  >({
    mutationFn: createCareerTransitionProfileApi,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.CAREER.PROFILE, data);
    },
  });

  const updateProfileMutation = useMutation<
    CareerTransitionProfile,
    CrewAheadApiError,
    Partial<CareerTransitionProfile>
  >({
    mutationFn: updateCareerTransitionProfileApi,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.CAREER.PROFILE, data);
    },
  });

  const activateMutation = useMutation<
    CareerTransitionProfile,
    CrewAheadApiError,
    number[]
  >({
    mutationFn: activateTransitionProfileApi,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.CAREER.PROFILE, data);
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CAREER.SUMMARY });
    },
  });

  const updateTargetPositionsMutation = useMutation<
    CareerTransitionProfile,
    CrewAheadApiError,
    number[]
  >({
    mutationFn: updateCareerTargetPositionsApi,
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEYS.CAREER.PROFILE, data);
      queryClient.invalidateQueries({ queryKey: ['career'] });
    },
  });

  const deactivateMutation = useMutation<void, CrewAheadApiError, void>({
    mutationFn: deactivateTransitionProfileApi,
    onSuccess: () => {
      // Invalidate all career keys on deactivation
      queryClient.invalidateQueries({ queryKey: ['career'] });
    },
  });

  // ─── Recalculate Mutation ──────────────────────────────────────────────────

  const recalculateMutation = useMutation<
    { detail: string; recommendations: unknown[] },
    CrewAheadApiError,
    void
  >({
    mutationFn: recalculateRecommendationsApi,
    onSuccess: () => {
      // Invalidate all career queries to refetch with new data
      queryClient.invalidateQueries({ queryKey: ['career'] });
    },
  });

  // ─── Callback Wrappers ─────────────────────────────────────────────────────

  const createProfile = useCallback(
    async (
      data: Partial<CareerTransitionProfile>
    ): Promise<CareerTransitionProfile> => {
      return createProfileMutation.mutateAsync(data);
    },
    [createProfileMutation]
  );

  const updateProfile = useCallback(
    async (
      data: Partial<CareerTransitionProfile>
    ): Promise<CareerTransitionProfile> => {
      return updateProfileMutation.mutateAsync(data);
    },
    [updateProfileMutation]
  );

  const activateTransition = useCallback(
    async (targetPositionIds: number[]): Promise<void> => {
      await activateMutation.mutateAsync(targetPositionIds);
    },
    [activateMutation]
  );

  const updateTargetPositions = useCallback(
    async (targetPositionIds: number[]): Promise<CareerTransitionProfile> => {
      return updateTargetPositionsMutation.mutateAsync(targetPositionIds);
    },
    [updateTargetPositionsMutation]
  );

  const deactivateTransition = useCallback(async (): Promise<void> => {
    await deactivateMutation.mutateAsync();
  }, [deactivateMutation]);

  const recalculate = useCallback(async (): Promise<void> => {
    await recalculateMutation.mutateAsync();
  }, [recalculateMutation]);

  // ─── Refetch Helpers ───────────────────────────────────────────────────────

  const refetchAll = useCallback(async (): Promise<void> => {
    await Promise.all([
      refetchProfileQuery(),
      refetchSummaryQuery(),
      refetchReadinessQuery(),
      refetchCareerPathQuery(),
      refetchSkillGapsQuery(),
      refetchCoursesQuery(),
      refetchBarriersQuery(),
      refetchShoreJobsQuery(),
    ]);
  }, [
    refetchProfileQuery,
    refetchSummaryQuery,
    refetchReadinessQuery,
    refetchCareerPathQuery,
    refetchSkillGapsQuery,
    refetchCoursesQuery,
    refetchBarriersQuery,
    refetchShoreJobsQuery,
  ]);

  // ─── Derived State ─────────────────────────────────────────────────────────

  const transitionProfile = transitionProfileData ?? null;
  const actualIsTransitionActive = transitionProfile?.isActive ?? false;
  const summary = summaryData ?? null;
  const readinessScore = readinessData ?? null;
  const careerPath = careerPathData ?? null;
  const skillGaps = skillGapsData ?? null;
  const courses = coursesData ?? [];
  const barriers = barriersData ?? null;
  const shoreJobs = shoreJobsData ?? [];

  const isProfileMutating =
    createProfileMutation.isPending ||
    updateProfileMutation.isPending ||
    updateTargetPositionsMutation.isPending ||
    activateMutation.isPending ||
    deactivateMutation.isPending;

  return {
    // Profile
    transitionProfile,
    isProfileLoading,
    isProfileFetching,
    isTransitionActive: actualIsTransitionActive,
    createProfile,
    updateProfile,
    updateTargetPositions,
    activateTransition,
    deactivateTransition,
    isProfileMutating,
    profileError: profileError ?? null,

    // Summary
    summary,
    isSummaryLoading,
    isSummaryFetching,
    summaryError: summaryError ?? null,

    // Readiness
    readinessScore,
    isReadinessLoading,
    isReadinessFetching,
    readinessError: readinessError ?? null,

    // Career Path
    careerPath,
    isCareerPathLoading,
    isCareerPathFetching,
    careerPathError: careerPathError ?? null,

    // Skill Gaps
    skillGaps,
    isSkillGapsLoading,
    isSkillGapsFetching,
    skillGapsError: skillGapsError ?? null,

    // Courses
    courses,
    isCoursesLoading,
    isCoursesFetching,
    coursesError: coursesError ?? null,

    // Barriers
    barriers,
    isBarriersLoading,
    isBarriersFetching,
    barriersError: barriersError ?? null,

    // Shore Jobs
    shoreJobs,
    isShoreJobsLoading,
    isShoreJobsFetching,
    shoreJobsError: shoreJobsError ?? null,

    // Target Position
    targetPositionId,
    setTargetPositionId,

    // Recalculate
    recalculate,
    isRecalculating: recalculateMutation.isPending,
    recalculateError: recalculateMutation.error ?? null,

    // Refetch
    refetchAll,
  };
}
